import { useCallback, useMemo } from 'react';
import useSite from '~/client/hooks/useSite';
import getDisplayOrder from './getDisplayOrder';
import { useTournamentsCountQuery } from '../../graphql/TournamentsCountQuery.generated';
import {
  useTournamentsQuery,
  TournamentsQueryVariables,
  TournamentsQuery,
} from '../../graphql/TournamentsQuery.generated';

type Props = {
  status?: TournamentStatus;
  first?: number;
  onlyMine?: boolean;
  excludeId?: string;
  gameId?: string;
};

const useTournaments = ({
  status,
  first,
  onlyMine,
  excludeId,
  gameId,
}: Props) => {
  const { site } = useSite();
  const variables: TournamentsQueryVariables = useMemo(
    () => ({
      site,
      onlyCurrent: status === 'current' && true,
      onlyFuture: status === 'future' && true,
      onlyPast: status === 'past' && true,
      onlyMy: onlyMine,
      gameId,
    }),
    [site, status, onlyMine, gameId],
  );

  const { data: countQuery, loading: loadingCount } = useTournamentsCountQuery({
    variables,
  });

  const { data, loading, error, fetchMore } = useTournamentsQuery({
    variables: {
      ...variables,
      first: first ?? 7,
      orderBy: getDisplayOrder(status),
      excludeTournamentIds: excludeId,
    },
  });

  const totalCount = countQuery?.casinoTournamentsPublic?.tournamentsCount;

  const endCursor =
    data?.casinoTournamentsPublic?.tournaments?.pageInfo?.endCursor;
  const hasNextPage =
    data?.casinoTournamentsPublic?.tournaments?.pageInfo?.hasNextPage;

  const onLoadMore = useCallback(() => {
    fetchMore({
      variables: {
        ...variables,
        first: 15,
        orderBy: getDisplayOrder(status),
        excludeTournamentIds: excludeId,
        after: endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const { casinoTournamentsPublic } = fetchMoreResult;
        const { tournaments } = fetchMoreResult?.casinoTournamentsPublic || {};

        const newResult = {
          casinoTournamentsPublic: {
            ...casinoTournamentsPublic,
            tournaments: {
              ...tournaments,
              edges: [
                ...(prev?.casinoTournamentsPublic?.tournaments?.edges ?? []),
                ...(fetchMoreResult?.casinoTournamentsPublic?.tournaments
                  ?.edges ?? []),
              ],
            },
          },
        };

        return newResult as TournamentsQuery;
      },
    });
  }, [endCursor, fetchMore, variables, excludeId, status]);

  return {
    loading: loading || loadingCount,
    error,
    tournaments: data?.casinoTournamentsPublic?.tournaments?.edges,
    totalCount,
    onLoadMore,
    hasNextPage,
  };
};

export default useTournaments;
